import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import store from '../../store';
import { hasMarkStateSelector } from '../../store/mark/selector';
import { MARK_STATE } from '../../store/mark/types';

const {
  EVENTS,
  getToolState,
  store: { state },
  importInternal,
} = cornerstoneTools;

const draw = importInternal('drawing/draw');
const drawRect = importInternal('drawing/drawRect');
const drawHandles = importInternal('drawing/drawHandles');
const setShadow = importInternal('drawing/setShadow');
const getNewContext = importInternal('drawing/getNewContext');
const getROITextBoxCoords = importInternal('util/getROITextBoxCoords');
const triggerEvent = importInternal('util/triggerEvent');

let isEdit = false;
let isCreate = false;
store.subscribe(() => {
  const state = store.getState();
  const hasMarkState = hasMarkStateSelector(state);
  isCreate = !hasMarkState(MARK_STATE.ENABLED_CREATE);
  isEdit = !hasMarkState(MARK_STATE.ENABLED_EDIT);
});

function defaultFreehandConfiguration() {
  return {
    handleRadius: 0.1,
    currentTool: -1,
  };
}

export default class MarkRectangleRoiTool extends cornerstoneTools.RectangleRoiTool {
  constructor(props = {}) {
    super({
      name: 'MarkRectangleRoi',
      configuration: defaultFreehandConfiguration(),
      ...props,
    });

    this._editMouseUpCallback = this._editMouseUpCallback.bind(this);
  }

  _editMouseUpCallback(evt) {
    const { element } = evt.detail;

    this._deactivateModify(element);
    this.endDrawing(element);
  }

  _activateModify(element) {
    element.addEventListener(EVENTS.MOUSE_CLICK, this._editMouseUpCallback);
  }
  _deactivateModify(element) {
    element.removeEventListener(EVENTS.MOUSE_CLICK, this._editMouseUpCallback);

    state.isToolLocked = false;
  }

  createNewMeasurement(evt) {
    if (!isCreate) return;
    return super.createNewMeasurement(evt);
  }

  renderToolData(evt) {
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    const eventData = evt.detail;
    const { image, element } = eventData;
    const lineDash = cornerstoneTools.getModule('globalConfiguration')
      .configuration.lineDash;
    const {
      handleRadius,
      drawHandlesOnHover,
      hideHandlesIfMoving,
      renderDashed,
    } = this.configuration;
    const context = getNewContext(eventData.canvasContext.canvas);

    // Meta
    const seriesModule =
      cornerstone.metaData.get('generalSeriesModule', image.imageId) || {};

    // Pixel Spacing
    const modality = seriesModule.modality;

    draw(context, context => {
      // If we have tool data for this element - iterate over each set and draw it
      for (let i = 0; i < toolData.data.length; i++) {
        const data = toolData.data[i];

        if (data.visible === false) {
          continue;
        }

        // Configure
        const color = cornerstoneTools.toolColors.getColorIfActive(data);
        const handleOptions = {
          color,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
          hideHandlesIfMoving,
        };

        setShadow(context, this.configuration);

        const rectOptions = { color };

        if (renderDashed) {
          rectOptions.lineDash = lineDash;
        }

        // Draw
        drawRect(
          context,
          element,
          data.handles.start,
          data.handles.end,
          rectOptions,
          'pixel',
          data.handles.initialRotation
        );

        if (this.configuration.drawHandles) {
          drawHandles(context, eventData, data.handles, handleOptions);
        }

        // Update textbox stats
        if (data.invalidated === true) {
          if (data.cachedStats) {
            this.throttledUpdateCachedStats(image, element, data);
          } else {
            this.updateCachedStats(image, element, data);
          }
        }

        // Default to textbox on right side of ROI
        if (!data.handles.textBox.hasMoved) {
          const defaultCoords = getROITextBoxCoords(
            eventData.viewport,
            data.handles
          );

          Object.assign(data.handles.textBox, defaultCoords);
        }

        data.unit = _getUnit(modality, this.configuration.showHounsfieldUnits);
      }
    });
  }

  handleSelectedCallback(evt, toolData, handle, interactionType = 'mouse') {
    const { element } = evt.detail;
    if (!isEdit) {
      this.triggerEvent(element, EVENTS.MEASUREMENT_COMPLETED, toolData);
      return preventPropagation(evt);
    }
    const toolState = getToolState(element, this.name);
    const config = this.configuration;

    for (let toolIndex = 0; toolIndex < toolState.data.length; toolIndex++) {
      const { start, end } = toolState.data[toolIndex].handles;
      const points = [start, end];

      for (let p = 0; p < points.length; p++) {
        if (points[p] === handle) {
          config.currentTool = toolIndex;
        }
      }
    }

    this._activateModify(element);
    return super.handleSelectedCallback(evt, toolData, handle, interactionType);
  }

  toolSelectedCallback(evt, toolData, interactionType = 'mouse') {
    const { element } = evt.detail;
    const toolState = getToolState(element, this.name);
    const config = this.configuration;
    for (let toolIndex = 0; toolIndex < toolState.data.length; toolIndex++) {
      if (toolState.data[toolIndex].uuid === toolData.uuid)
        config.currentTool = toolIndex;
    }
    this._activateModify(element);
    return super.toolSelectedCallback(evt, toolData, interactionType);
  }

  endDrawing(element) {
    const toolState = getToolState(element, this.name);
    const config = this.configuration;
    const data = toolState.data[config.currentTool];
    this.triggerEvent(element, EVENTS.MEASUREMENT_COMPLETED, data);
  }

  triggerEvent(element, eventType, measurementData) {
    const toolName = measurementData.toolType || measurementData.toolName;
    const eventData = {
      toolName,
      toolType: toolName, // Deprecation notice: toolType will be replaced by toolName
      element,
      measurementData,
    };
    triggerEvent(element, eventType, eventData);
  }
}

function _getUnit(modality, showHounsfieldUnits) {
  return modality === 'CT' && showHounsfieldUnits !== false ? 'HU' : '';
}

function preventPropagation(evt) {
  evt.stopImmediatePropagation();
  evt.stopPropagation();
  evt.preventDefault();
}
